import { Vue, Component, InjectReactive, Inject, Prop, Watch } from 'vue-property-decorator';
import {
  Step, StepPager, Btn, Toolbar, Field, DropdownField, IStepComponent, ToolbarTabContainer, ToolbarTab, NextStepFunction,
  ClearAfterFunction, GetService, IMapping, IMappingDetail, Action, Tooltip, IField
} from '@samc/centricity-core';
import { AjaxHelper, AjaxHelperException, Guid, errToMessage } from '@samc/common';
import { ScratchCompare } from '@samc/querybuilder/core/ClientApp/modular/Scratchpad/CompareTool/ScratchCompare';
import { ComparePivotContainerProps } from '@samc/querybuilder/core/ClientApp/modular/Scratchpad/DataCompare/ComparePivotContainer';
import { ConfigurationContextProps, QueryProps } from '@samc/querybuilder';
import { IEnrichment, IExecuteRequest, ILsMappedField } from '@samc/querybuilder-ui';
import { download } from '@/utils';
import { IPackageVersion, ILsDomainField } from '@/Interfaces';
import ImportAutosave from '@/Store/ImportAutoSave';
import store from '@/Store/store';
import ComparisionFlow from '@samc/querybuilder/core/ClientApp/modular/Scratchpad/model/ComparisionFlow';

interface ILSField extends IField {
  lsFieldName: string;
}

@Component({
  name: 'enrichment-step',
  components: {
    Step, StepPager, Btn, Toolbar, Field, DropdownField, ToolbarTabContainer, ToolbarTab,
    Compare: ScratchCompare as any, Action, Tooltip
  },
  store: store
})
export default class EnrichmentStepComponent extends Vue implements IStepComponent {
  public description: string = '';
  public isValid: boolean = false;
  public initialized: boolean = false;
  public isDirty: boolean = false;
  public isReadonly: boolean = false;
  public tab: 'rules' | 'results' = 'rules';
  public compareKey: number = 0;
  public showCompare: boolean = false;
  public id: string = 'enrichment';
  public scratchPadNode: string = '';
  public helper: AjaxHelper | null = null;
  public enrichmentPath: string = '';
  public resultsToolTip: string = "Results";
  public popoutUrl: string = '';
  public popoutWindow: Window | null = null;
  public mappedFields: string[] = [];
  public lsMappedFields: ILsMappedField[] = [];
  public mappedFieldsLoaded: boolean = false;
  public comparisionFlow: ComparisionFlow = ComparisionFlow.EnrichAndCompare;

  public sessionId: string = Guid.create().toString();

  public compareTabProps: ComparePivotContainerProps = {
    hideOnlyInSource: true,
    hideOnlyInTarget: true,
    inBothButDifferentText: 'Differences',
    /* Greg confirmed that following three props for use with internal tools and can be safety be empty for external usage */
    children: undefined
  }

  /**
   * The wrapper for the component instance
   */
  public get component(): IStepComponent | null {
    return {
      isValid: this.isValid,
      isDirty: this.isDirty,
      isReadonly: this.isReadonly,
      clear: this.clear,
      initializeEvents: this.initializeEvents,
      clearEvents: this.clearEvents,
      initialize: this.initialize,
      enter: this.enter,
      skip: this.skip,
      leave: this.leave
    };
  };

  @Prop({ required: true }) public domainId!: number;
  @Prop({ required: true }) public domainName!: number;
  @Prop({ required: true }) public scratchPadUrl!: string;
  @Prop({ required: true }) public lsUrl!: string;
  @Prop({ default: '' }) public packageId!: string;
  @Prop({ default: '-1' }) public loanListId!: string;
  @Prop() public value!: boolean;
  @Prop({ type: Boolean, default: false }) preventSkip!: boolean;
  @Prop({ default: '/api/' }) public apiRoot?: string;
  @Prop({ default: true }) public showDevMessages!: boolean;
  @Prop({ default: 'Enrichment' }) public title!: string;

  @Prop() public fileName!: string;
  @Prop() public enrichedFileName!: string;
  @Prop({ default: '' }) public currentData!: string;

  /**
   * Injector to get the next method from the DataImport parent to create a standard way of moving to the next step
   */
  @Inject('next') public next!: NextStepFunction;
  @Inject('clearAfter') public clearAfter!: ClearAfterFunction;
  @Inject('getService') public getService!: GetService;
  @InjectReactive() public mapping!: IMapping<IMappingDetail> | null;
  @InjectReactive() public readonly extraParams!: Record<string, string>;

  public get domain(): string {
    return String(this.domainName);
  }

  public get editorReady(): boolean {
    return this.initialized && this.verificationToken.length > 0 && this.mappedFieldsLoaded;
  }

  public get enrichmentRequest(): IExecuteRequest {
    return {
      domainId: String(this.domainId),
      externalSetIds: [this.loanListId],
    };
  }

  public get lsProps(): QueryProps & ConfigurationContextProps {
    return {
      antiforgeryToken: this.verificationToken,
      apiRoot: this.apiRoot,
      apiUrl: this.lsUrl,
      domainId: this.domainId,
      showDevMessages: this.showDevMessages,
      externalSetIds: [this.loanListId],
      showColumnFilters: true,
    }
  }

  public verificationToken: string = '';
  public token: string | null = null;

  public async mounted() {
    this.token = this.$token;
    this.verificationToken = await this.$verificationToken();
  }

  public async clear(): Promise<void> {
    // On Clear Event
    this.scratchPadNode = '';
    this.description = '';
    this.isValid = true;
    this.initialized = false;
    this.isDirty = false;
    this.isReadonly = false;
    this.tab = 'rules';
    this.compareKey = 0;
    this.showCompare = false;
    this.mappedFields = [];
    this.lsMappedFields = [];
    this.mappedFieldsLoaded = false;

    this.$emit('input', null);
    this.$emit('scratchpadnode', '');
  }

  public async initialize(): Promise<boolean> {
    this.helper = new AjaxHelper('/DataImport/Import', this.verificationToken, '', null);
    try {
      const enrichedPackage = await this.helper?.getData<IPackageVersion>('', { handler: 'LatestEnrichedPackageVersion', id: this.packageId });
      this.enrichmentPath = enrichedPackage?.enrichmentPath ?? '';
    } catch (ex: any) {
      this.$log('Error occured on initialization for enrichment step when gathering the latest enriched package version', ex);
    }

    if (this.extraParams['enrichmentPath']) {
      this.enrichmentPath = this.extraParams['enrichmentPath'];
    }
    this.description = this.enrichmentPath;
    this.initialized = true;
    return true;
  }

  public getData(): any {
    return {
      id: this.id,
      preventSkip: this.preventSkip,
      isValid: this.isValid,
      showCompare: this.showCompare,
      scratchPadNode: this.scratchPadNode,
      compareKey: this.compareKey,
      compareTabProps: this.compareTabProps,
      tab: this.tab
    }
  }

  public async enter(): Promise<void> {
    // On Enter Event
    if (this.extraParams['enrichmentPath']) {
      this.enrichmentPath = this.extraParams['enrichmentPath'];
      this.description = this.enrichmentPath;
    }

    // Pre-load mapped fields so we can inject them while the scratchpad nodes are being created
    const fieldService = this.getService('field');
    if (fieldService !== null) {
      const fieldIds = this.mapping?.details
        .map(x => x.internalFieldId)
        .filter(x => x !== undefined && x !== null && x > 0) as number[] | undefined;

      if (fieldIds !== undefined && fieldIds.length > 0) {
        this.mappedFields = (await fieldService.getFields<ILSField>({ fieldIds }))
          .items
          .filter(x => x.lsFieldName !== null)
          .map(x => `!ALT<GOLDEN>${x.lsFieldName}`);
      }
    }

    await this.loadMappedDomainFields();
    this.mappedFieldsLoaded = true;
  }

  public async skip(): Promise<void> {
    // On Skip
  }

  /**
   * Intialize events for the event bus
   **/
  public async initializeEvents(): Promise<void> {
    // Do Nothing
  }

  /**
   * Remove events from the event bus
   **/
  public async clearEvents(): Promise<void> {
    // Do Nothing
  }

  public async leave(dir: 1 | -1): Promise<boolean> {
    if (this.isDirty) {
      return await this.$confirm.warning('Leaving the page', 'You have unsaved data. If you proceed your changes will be lost.');
    }
    return true;
  }

  public async beforeMount() {
    window.addEventListener("beforeunload", await this.preventNav);
  }

  public async preventNav(event: any) {
    if (!this.isDirty) return;
    event.preventDefault();
    event.returnValue = "";
  }

  public async skipEnrichments() {
    await this.clearAfter(this.id);
    await this.clear();
    this.initialized = true;
    this.description = 'Skipped Enrichment';
    this.$emit('input', null);
    await this.next();
  }

  public onEnrichmentSaved(event: CustomEvent<IEnrichment>) {
    void this.enrichmentSaved(event.detail);
  }

  public onEnrichmentApply(event: CustomEvent<IEnrichment>) {
    void this.enrichmentApply(event.detail);
  }

  public onEnrichmentDirty(event: CustomEvent<boolean>) {
    this.isDirty = event.detail;
    if (event.detail) {
      this.isValid = false;
    }
  }

  public onEnrichmentLoaded(event: CustomEvent<IEnrichment | null>) {
    const enrichment = event.detail;
    this.description = enrichment !== null ? this.enrichmentPathValue(enrichment) : '';
    this.isDirty = false;
    this.isValid = false;
  }

  private enrichmentPathValue(enrichment: IEnrichment): string {
    if (enrichment.enrichmentPath !== null && enrichment.enrichmentPath !== undefined) {
      return String(enrichment.enrichmentPath);
    }
    return '';
  }

  public async enrichmentSaved(enrichment: IEnrichment) {
    this.description = this.enrichmentPathValue(enrichment);
    this.$emit('input', JSON.parse(JSON.stringify(enrichment)));
    this.$emit('updatedcurrentdata', JSON.stringify(this.getData()));
  }

  public async enrichmentApply(enrichment: IEnrichment) {
    this.$loading('Generating Compare');
    this.description = this.enrichmentPathValue(enrichment);
    this.$emit('input', JSON.parse(JSON.stringify(enrichment)));
    this.$emit('updatedcurrentdata', JSON.stringify(this.getData()));
    await this.clearAfter(this.id);

    try {
      const enrichmentPath = this.enrichmentPathValue(enrichment);
      this.description = enrichmentPath;
      let fields = await this.getEnrichmentFields(enrichmentPath);
      fields = fields.concat(this.mappedFields.filter(x => fields.indexOf(x) === -1));

      const response = await this.enrichData(enrichmentPath, fields);
      this.showCompare = true;
      this.compareKey += 1;
      this.isValid = true;
      this.tab = 'results';

      this.$emit('input', JSON.parse(JSON.stringify(enrichment)));
      this.$emit('scratchpadnode', response.EnrichedNodeId);

      this.scratchPadNode = response.EnrichedNodeId;

      this.$emit('updatedcurrentdata', JSON.stringify(this.getData()));
      this.isDirty = false;
      if (this.popoutWindow !== null) {
        this.openPopout();
      }
    } catch (ex: any) {
      if (ex instanceof AjaxHelperException && ex.data?.Messages) {
        this.$alert({
          type: 'error',
          title: 'The following errors occurred!',
          errors: ex.data.Messages
        });
      }
      else if (ex instanceof AjaxHelperException && ex.data?.Errors?.ExpressionValidations) {
        this.$alert({
          type: 'error',
          title: 'The following errors occurred!',
          errors: ex.data.Errors.ExpressionValidations
        });
      }
      else {
        this.$alert.error('An Error Occurred');
      }
    } finally {
      this.$loading();
    }
  }

  /**
   * Method is meant to get a list of fields from the scratch pad api to determine what fields should be used when going through the
   * enrichment process
   *
   * @param enrichmentPath The enrichment path that was gathered from the save/apply event coming from the enrichment editor
   */
  private async getEnrichmentFields(enrichmentPath: string): Promise<string[]> {
    const data = {
      DomainName: this.domain,
      EnrichmentPath: enrichmentPath,
      PackageId: this.packageId,
      FileName: this.fileName,
      EnrichedFileName: this.enrichedFileName
    };

    const helper = new AjaxHelper(this.scratchPadUrl, this.verificationToken, null, this.$token); 
    helper.options('/api/Scratchpad/Enrichment', data);
    return await helper.options('/api/Scratchpad/Enrichment', data);
  }

  /**
   * Method is meant to enrich the data set
   *
   * @param enrichmentPath The enrichment path that was gathered from the save/apply event coming from the enrichment editor
   * @param fields The fields the were looked up by another api call
   */
  private async enrichData(enrichmentPath: string, fields: any) {
    const data = {
      EnrichmentPath: enrichmentPath,
      EnrichedFileName: this.enrichedFileName,
      PackageId: this.packageId,
      FileName: this.fileName,
      domainId: this.domainId,
      fields,
      externalSetIds: [this.loanListId]
    };
    
    /// TODO: Move this request to the @samc/query-builder-client npm package with proper typing
    const helper = new AjaxHelper(this.lsUrl, this.verificationToken, null, this.$token);
    return await helper.postData<any>('/api/DynamicScratchpad/ExecAndEnrich', data, { withCredentials: true });
  }

  private async deleteScratchpadNode() {
    const helper = new AjaxHelper(this.lsUrl, this.verificationToken, null, this.$token);
    await helper.delete(`/api/Scratchpad/Dataset/${this.domainName}/${this.packageId}`, { withCredentials: true });
  }

  /**
   * Method is to handle the endpoint api for the compare component and will return a compare between the 9a and 9b
   **/
  public async compareEndpoint() {
    const helper = new AjaxHelper(this.lsUrl, this.verificationToken, null, this.$token);
    const query = {
      leftname: this.fileName,
      rightname: this.enrichedFileName,
      compareKey: 'LoanId',

      sessionId: this.sessionId
    }

    return await helper
      .postData(`/api/Compare/Compare/${this.packageId}`, query, { withCredentials: true })
      .catch((ex: any) => {
        if (ex instanceof AjaxHelperException && ex.data?.Messages) {
          this.$alert({
            type: 'error',
            title: 'The following errors occurred!',
            errors: ex.data.Messages
          });
        } else {
          this.$alert.error('Error occurred during merge process');
        }
      });
  }

  public async onExport(options: Record<string, any>) {
    const helper = new AjaxHelper(this.scratchPadUrl ?? '', null, null, null);
    let query = {
      leftLabel: "Left",
      rightLabel: "Right",
      leftname: this.fileName,
      rightname: this.enrichedFileName,
      leftKey: 'LoanId',
      rightKey: 'LoanId',
      comparisonFlow: 2,
    }

    if (options) {
      query = { ...query, ...options };
    }
    const url = `/api/Scratchpad/ExportWithCompareUsingLeftRightColumns/${this.packageId}`;
    const data = await helper.getData<Blob>(url, query, { responseType: 'blob' });
    download(data, 'EnrichmentResults.xlsx', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
  }

  /**
   * Will parse the scratchpad url given and add a / if one does not exist on the end
   */
  public get parsedLsUrl(): string {
    const url = this.lsUrl ?? '';
    if (url.endsWith('/')) return url + 'api/';
    return url + '/api/';
  }

  private async loadMappedDomainFields(): Promise<void> {
    if (this.lsMappedFields.length > 0) {
      return;
    }

    if (this.packageId.length === 0) {
      this.lsMappedFields = [];
      return;
    }

    try {
      this.$loading('Loading Mapped Fields');
      const helper = new AjaxHelper('/datasource', this.verificationToken, '', this.token);
      const lsFields = await helper.getData<ILsDomainField[]>(`packages/${this.packageId}/lsmappedfields`);
      this.lsMappedFields = lsFields.filter(x =>
        x.lsFieldName !== null && x.lsFieldName !== undefined && x.lsFieldName.length > 0
      );
    } catch (err: unknown) {
      this.$log('An error occurred when loading mapped fields', err);
      this.lsMappedFields = [];
      this.$alert({
        type: 'error',
        title: 'An error occurred when loading mapped fields',
        errors: errToMessage(err),
        closingOption: 'holdOpen'
      });
    } finally {
      this.$loading();
    }
  }

  public async openPopout() {
    this.popoutUrl = `/dataimport/import/compareresult?&packageid=${this.packageId}&filename=${this.fileName}&eFileName=${this.enrichedFileName}`;
    const name = `compareresult_${this.packageId}_${this.domainId}`;
    if (this.popoutWindow !== null) {
      this.popoutWindow.close();
    }
    const width = document.documentElement.clientWidth * .85;
    const height = document.documentElement.clientHeight * .85;
    this.popoutWindow = window.open(this.popoutUrl, name, `width=${width},height=${height}`);
    let popout = this.popoutWindow;
    window.onbeforeunload = function () {
      popout?.close();
    };
  }

  @Watch('extraParams', { immediate: true })
  public async onExtaParamChange(newValue?: Record<string, string>) {
    await ImportAutosave.setState({ extraParams: newValue });
  }
}